import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useGlobals from './useGlobals';
import { setChat, setUpdateChat } from '../Store/Actions/chatActions';
import { RootState } from '../Store/Reducers';
import { setDragZoneInputProps, setDragZoneRootProps } from '../Store/Actions/environmentActions';
import { Chat, ChatList, Prompt } from '../Types';
import { DLPState } from '../Types/Enums';
import useSocketMessageHandler from './useSocketMessageHandler';
import useClipboard from './useClipboard';
import useDocuments from './useDocuments';
import useDragAndDrop from './useDragDrop';
import { chatService } from '../Services/chatService';
import { settingsService } from '../Services/settingsService';

interface ChatInputProps {
    chat?: Chat;
    initMessage?: string;
}

const DEFAULT_CHAT = {} as Chat;

const useChatInput = ({ chat = DEFAULT_CHAT, initMessage }: ChatInputProps) => {
    const { dispatch, currentURL, i18n, windowWidth, getPrompt, getBreakpoint } = useGlobals();

    const { SocketMessageHandlerActions } = useSocketMessageHandler();

    const isPreviewPrompt = useMemo(() => !currentURL.includes('chat'), [currentURL]);
    const isHome = useMemo(() => currentURL.endsWith('/') || currentURL.includes('/start'), [currentURL]);

    const basePrompt = useSelector((state: RootState) => state.environment.basePrompt as Prompt);
    const selectedPromptIdentifier = useSelector(
        (state: RootState) =>
            (state.environment.selectedPrompt !== ''
                ? state.environment.selectedPrompt
                : basePrompt.identifier) as string,
    );
    const [chatPrompt, setChatPrompt] = useState<Prompt>(getPrompt(selectedPromptIdentifier) || basePrompt);
    const [localDlpActive, setLocaldlpActive] = useState<boolean>(
        (isHome !== undefined && isHome) ||
            chatPrompt?.combinedDlp === DLPState.dlp_on ||
            chatPrompt?.combinedDlp === DLPState.dlp_optional,
    );

    const isStopable = useSelector((state: RootState) => state.chats.isStopable as boolean);
    const chats = useSelector((state: RootState) => state.chatlist.chatlist as ChatList[]);
    const featuresDocumentUpload = useSelector((state: RootState) => state.settings.features.documentUpload as boolean);
    const isSending = useSelector((state: RootState) => state.chats.isSending as boolean);

    const [images, setImages] = useState<{ url: string; sha1: string }[]>([]);
    const [files, setFiles] = useState<{ name: string; url: string; sha1: string }[]>([]);
    const [tmpImageUrls, setTmpImageUrls] = useState<string[]>([]);
    const [tmpFileNames, setTmpFileNames] = useState<string[]>([]);
    const [fileTypes, setFileTypes] = useState<{ [key: string]: string[] }>({});
    const [allMimeTypesWithFileExtensions, setAllMimeTypesWithFileExtensions] = useState<{ [key: string]: string[] }>(
        {},
    );

    const [fileHoverIndex, setFileHoverIndex] = useState<number>(-1);
    const [imageHoverIndex, setImageHoverIndex] = useState<number>(-1);
    const [newMessage, setNewMessage] = useState<string>(initMessage ?? '');
    const [numOfLines, setNumOfLines] = useState<number>(0);
    const [showIcons, setShowIcons] = useState<boolean>(false);
    const [showExamples, setShowExamples] = useState<boolean>(false);
    const [userTyped, setUserTyped] = useState<boolean>(false);

    const imageUploadButtonRef: RefObject<HTMLInputElement> = useRef(null!);
    const textInputRef: RefObject<HTMLTextAreaElement> = useRef(null!);
    const ToggleInputFeaturesRef: RefObject<HTMLDivElement> = useRef(null!);
    const timerId = useRef<NodeJS.Timeout | null>(null);

    const [height, setHeight] = useState<number>(textInputRef?.current?.clientHeight || 44);
    const [ToggleInputFeaturesWidth, setToggleInputFeaturesWidth] = useState<number>(80);

    const [showPiiText, setShowPiiText] = useState(false);
    const [originalMessage, setOriginalMessage] = useState('');
    const [shouldSendAfterDlpChange, setShouldSendAfterDlpChange] = useState(false);
    const [fullDlpBlocked, setFullDlpBlocked] = useState(false);

    const { DocumentsActions, resizeFileHandler } = useDocuments({
        setFiles,
        setTmpFileNames,
        textInputRef,
        setImages,
        setTmpImageUrls,
        model: chatPrompt,
        i18n,
    });

    const { ClipboardActions } = useClipboard({
        setTmpFileNames,
        setFiles,
        setNewMessage,
        textInputRef,
    });

    const { getRootProps, getInputProps } = useDragAndDrop({
        fileTypes,
        dispatch,
        chatPrompt,
        selectedPromptIdentifier,
        setTmpFileNames,
        setFiles,
        i18n,
        combinedDlp: chatPrompt.combinedDlp ?? DLPState.dlp_on,
    });

    const handleDlpActiveCheckbox = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isHome) {
                dispatch(setChat({ ...chat, dlp_active: event.target.checked ? true : false }));
                dispatch(setUpdateChat(true));
            }
        },
        [isHome, dispatch, chat],
    );

    const stopStream = useCallback(async () => {
        SocketMessageHandlerActions.stopStream();
    }, [SocketMessageHandlerActions]);

    const handleCrossClick = useCallback(() => {
        setShowIcons(false);
        textInputRef.current?.focus();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            DocumentsActions.handleFileChange(event, chatPrompt.identifier, chatPrompt.combinedDlp ?? DLPState.dlp_on);
        },
        [DocumentsActions, chatPrompt.identifier, chatPrompt.combinedDlp],
    );

    const handleFileDelete = useCallback(
        (index: number) => {
            DocumentsActions.handleFileDelete(index);
        },
        [DocumentsActions],
    );

    const handleImageChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            DocumentsActions.handleImageChange(event);
        },
        [DocumentsActions],
    );

    const handleImageDelete = useCallback(
        (index: number) => {
            DocumentsActions.handleImageDelete(index);
        },
        [DocumentsActions],
    );

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewMessage(() => event.target.value);
        setShowPiiText(false);
        if (event.target.value.length > 0) {
            setShowIcons(false);
        } else {
            setShowIcons(true);
        }
    }, []);

    const handleDlpChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        textInputRef.current?.focus();
    }, []);

    const handlePaste = useCallback(
        (event: React.ClipboardEvent) => {
            ClipboardActions.handlePaste(event, newMessage, chatPrompt?.combinedDlp ?? DLPState.dlp_on, fileTypes);
        },
        [ClipboardActions, newMessage, chatPrompt.combinedDlp, fileTypes],
    );

    const handlePlusClick = useCallback(() => {
        setShowIcons(true);
        textInputRef.current?.focus();
    }, []);

    const sendMessageHandler = useCallback(
        (example?: string, dlp_active: boolean = true) => {
            SocketMessageHandlerActions.sendMessage(
                chat.identifier ?? '',
                example ?? newMessage,
                chatPrompt.identifier ?? basePrompt.identifier,
                chats,
                dlp_active,
                images,
                files,
                [
                    () => setNewMessage(''),
                    () => setImages([]),
                    () => setTmpImageUrls([]),
                    () => setFiles([]),
                    () => setTmpFileNames([]),
                    () => setShowIcons(true),
                    () => textInputRef.current?.focus(),
                ],
            );
            setShowIcons(true);
        },
        [chat, SocketMessageHandlerActions, newMessage, images, files, chatPrompt, chats, basePrompt],
    );

    const handleDlpSendMessage = useCallback(
        async (overrideMessage?: string) => {
            if (isSending) return;
            setFullDlpBlocked(false);
            if (chatPrompt.combinedDlp === DLPState.dlp_optional) {
                try {
                    const messageToCheck = overrideMessage ? overrideMessage : newMessage;
                    setOriginalMessage(messageToCheck);
                    const response = await chatService.checkDlp(messageToCheck, chat?.identifier || '');

                    if (response.data.error) {
                        setNewMessage('〖' + response.data.error + '⟆Regex〗');
                        setShowPiiText(true);
                        if (textInputRef.current) {
                            textInputRef.current.value = '〖' + response.data.error + '⟆Regex〗';
                            textInputRef.current.blur();
                        }
                        setFullDlpBlocked(true);
                        return;
                    }

                    if (response.data.text && response.data.text !== messageToCheck) {
                        setNewMessage(response.data.text);
                        setShowPiiText(true);
                        if (textInputRef.current) {
                            textInputRef.current.value = response.data.text;
                            textInputRef.current.blur();
                        }
                        return;
                    }
                } catch (error) {
                    return;
                }
            }

            setShowPiiText(false);
            const messageToSend = overrideMessage ? overrideMessage : newMessage;
            setNewMessage(messageToSend);
            if (textInputRef.current) {
                textInputRef.current.value = messageToSend;
            }
            sendMessageHandler(messageToSend, chatPrompt.combinedDlp !== DLPState.dlp_off);
        },
        [newMessage, chat?.identifier, sendMessageHandler, textInputRef, isSending, chatPrompt.combinedDlp],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey && windowWidth > getBreakpoint('sm')) {
                event.preventDefault();
                newMessage !== '' && handleDlpSendMessage();
            }
        },
        [handleDlpSendMessage, windowWidth, getBreakpoint, newMessage],
    );

    const handleExampleClick = useCallback(
        (example: string) => {
            handleDlpSendMessage(example);
        },
        [handleDlpSendMessage],
    );

    const calculateWidth = useCallback(() => {
        if (newMessage.length === 0 || showIcons) {
            return ToggleInputFeaturesWidth;
        }

        if ((newMessage.length !== 0 && !showIcons) || (showIcons && newMessage.length !== 0)) {
            return 40;
        }

        return 0;
    }, [ToggleInputFeaturesWidth, showIcons, newMessage]);

    useEffect(() => {
        if (Object.keys(allMimeTypesWithFileExtensions).length === 0) return;

        const fileTypes =
            chatPrompt.allowedMimeTypes?.reduce((acc: { [key: string]: string[] }, mimeType) => {
                if (allMimeTypesWithFileExtensions[mimeType]) {
                    return {
                        ...acc,
                        [mimeType.toLowerCase()]: allMimeTypesWithFileExtensions[mimeType].map(
                            (ext: string) => '.' + ext,
                        ),
                    };
                }
                return acc;
            }, {}) || {};

        setFileTypes(fileTypes);
    }, [chatPrompt.allowedMimeTypes, allMimeTypesWithFileExtensions]);

    useEffect(() => {
        settingsService.getAllMimeTypesAndFileExtensions().then((response) => {
            setAllMimeTypesWithFileExtensions(response.data);
        });
    }, []);

    useEffect(() => {
        setChatPrompt(getPrompt(selectedPromptIdentifier));
    }, [selectedPromptIdentifier, getPrompt]);

    useEffect(() => {
        setUserTyped(false);
        setShowExamples(false);
    }, [selectedPromptIdentifier]);

    useEffect(() => {
        if (timerId.current) {
            clearTimeout(timerId.current);
        }

        if (!userTyped) {
            timerId.current = setTimeout(() => {
                setShowExamples(true);
            }, 3000);
        }

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
    }, [userTyped, selectedPromptIdentifier]);

    useEffect(() => {
        if (newMessage.length > 0) {
            setUserTyped(true);
            setShowExamples(false);
        }
    }, [newMessage]);

    useEffect(() => {
        if (textInputRef.current) {
            const updateInputHeight = () => {
                if (textInputRef.current) {
                    const lineHeight = parseFloat(window.getComputedStyle(textInputRef.current).lineHeight);
                    const numberOfLines = Math.floor(textInputRef.current.scrollHeight / lineHeight);
                    setNumOfLines(numberOfLines);

                    const newHeight = textInputRef.current.getBoundingClientRect().height;
                    setHeight(newHeight);
                }
            };

            updateInputHeight();

            const resizeObserver = new ResizeObserver(() => {
                updateInputHeight();
            });

            resizeObserver.observe(textInputRef.current);

            return () => {
                if (textInputRef) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [newMessage, windowWidth]);

    useLayoutEffect(() => {
        let featureContainerWidth = 0;
        const updateWidth = () => {
            if (ToggleInputFeaturesRef.current) {
                featureContainerWidth = 0;
                Array.from(ToggleInputFeaturesRef.current.children).forEach((child: Element) => {
                    featureContainerWidth += child.getBoundingClientRect().width;
                });
                setToggleInputFeaturesWidth(featureContainerWidth);
            }
        };

        const observer = new ResizeObserver(() => {
            updateWidth();
        });

        if (ToggleInputFeaturesRef.current) {
            Array.from(ToggleInputFeaturesRef.current.children).forEach((child: Element) => {
                observer.observe(child);
            });
        }

        updateWidth();

        return () => {
            observer.disconnect();
        };
    }, [ToggleInputFeaturesRef, showIcons, newMessage, chat.dlp]);

    useEffect(() => {
        if (!isPreviewPrompt) {
            textInputRef.current?.focus();
        }
    }, [chat.identifier, chatPrompt, chat.dlp_active, isHome, isPreviewPrompt]);

    useEffect(() => {
        if (Object.keys(fileTypes).length > 0) {
            dispatch(setDragZoneRootProps(() => getRootProps()));
            dispatch(setDragZoneInputProps(() => getInputProps()));
        } else {
            dispatch(setDragZoneRootProps(() => {}));
            dispatch(setDragZoneInputProps(() => {}));
        }
        setFiles([]);
    }, [getRootProps, getInputProps, dispatch, fileTypes]);

    const ChatInputActions = useMemo(
        () => ({
            handleKeyDown,
            sendMessageHandler,
            handleInputChange,
            handleImageChange,
            handleImageDelete,
            handleFileChange,
            handleFileDelete,
            handlePlusClick,
            handleCrossClick,
            handlePaste,
            handleDlpChange,
            stopStream,
            handleDlpActiveCheckbox,
            handleExampleClick,
            calculateWidth,
            getBreakpoint,
            handleDlpSendMessage,
        }),
        [
            handleKeyDown,
            sendMessageHandler,
            handleInputChange,
            handleImageChange,
            handleImageDelete,
            handleFileChange,
            handleFileDelete,
            handlePlusClick,
            handleCrossClick,
            handlePaste,
            handleDlpChange,
            stopStream,
            handleDlpActiveCheckbox,
            handleExampleClick,
            calculateWidth,
            getBreakpoint,
            handleDlpSendMessage,
        ],
    );

    const sendMessage = useCallback(() => {
        setNewMessage(originalMessage);
        if (textInputRef.current) {
            textInputRef.current.value = originalMessage;
        }
        sendMessageHandler(originalMessage);
        setNewMessage('');
        if (textInputRef.current) {
            textInputRef.current.value = '';
        }
    }, [sendMessageHandler, originalMessage, textInputRef, setNewMessage]);

    const sendOriginalMessage = useCallback(() => {
        setNewMessage(originalMessage);
        if (textInputRef.current) {
            textInputRef.current.value = originalMessage;
        }
        sendMessageHandler(originalMessage, false);
        setNewMessage('');
        if (textInputRef.current) {
            textInputRef.current.value = '';
        }
    }, [sendMessageHandler, originalMessage, textInputRef, setNewMessage]);

    const handleTextareaClick = useCallback(() => {
        if (showPiiText) {
            setNewMessage(originalMessage);
            if (textInputRef.current) {
                textInputRef.current.value = originalMessage;
                textInputRef.current.focus();
            }
            setShowPiiText(false);
        }
    }, [showPiiText, originalMessage, textInputRef]);

    useEffect(() => {
        if (shouldSendAfterDlpChange) {
            sendOriginalMessage();
            setShouldSendAfterDlpChange(false);
        }
    }, [shouldSendAfterDlpChange, sendOriginalMessage]);

    return {
        ChatInputActions,
        isPreviewPrompt,
        tmpImageUrls,
        setImageHoverIndex,
        imageHoverIndex,
        handleImageDelete,
        tmpFileNames,
        files,
        fileHoverIndex,
        setFileHoverIndex,
        handleFileDelete,
        newMessage,
        setNewMessage,
        numOfLines,
        showIcons,
        ToggleInputFeaturesRef,
        ToggleInputFeaturesWidth,
        showExamples,
        imageUploadButtonRef,
        chatPrompt,
        i18n,
        textInputRef,
        height,
        isSending,
        isStopable,
        featuresDocumentUpload,
        localDlpActive,
        showPiiText,
        setShowPiiText,
        originalMessage,
        shouldSendAfterDlpChange,
        setShouldSendAfterDlpChange,
        fullDlpBlocked,
        sendMessage,
        handleDlpSendMessage,
        handleTextareaClick,
        fileTypes,
        windowWidth,
    };
};

export default useChatInput;
